import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  BarChart3, 
  Target, 
  Users, 
  TrendingUp, 
  FileText, 
  CheckCircle2, 
  Clock, 
  DollarSign,
  ArrowRight,
  Shield,
  Building2,
  Heart
} from 'lucide-react';

const ProjectManagementHub = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    // Check authentication - support both token systems
    const token = localStorage.getItem('token') || localStorage.getItem('xchange_token');
    const userData = localStorage.getItem('user') || localStorage.getItem('xchange_user');
    
    if (!token) {
      navigate('/xchange/login');
      return;
    }
    
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    
    // Fetch quick stats based on user role
    fetchStats(token, userData ? JSON.parse(userData) : null);
  }, [navigate]);
  
  const fetchStats = async (token, userData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_REACT_APP_BACKEND_URL;
      
      // Determine which stats endpoint to call based on user role
      let endpoint = '/api/saas/dashboard/admin';
      if (userData?.role === 'corporate') {
        endpoint = '/api/saas/dashboard/sponsor';
      } else if (userData?.role === 'ngo') {
        endpoint = '/api/saas/dashboard/agency';
      }
      
      const response = await fetch(`${backendUrl}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getRoleConfig = () => {
    const role = user?.role || 'admin';
    
    const configs = {
      corporate: {
        title: 'Sponsor Dashboard',
        description: 'Monitor funded projects, track milestones, and review impact reports',
        path: '/saas/sponsor/dashboard',
        icon: Building2,
        color: '#14B8A6',
        features: [
          'Real-time project tracking',
          'Milestone monitoring',
          'Financial utilization reports',
          'Impact metrics & KPIs'
        ]
      },
      ngo: {
        title: 'Agency Dashboard',
        description: 'Manage projects, submit updates, and track deliverables',
        path: '/saas/agency/dashboard',
        icon: Heart,
        color: '#14B8A6',
        features: [
          'Project management',
          'Progress updates submission',
          'Milestone tracking',
          'Communication hub'
        ]
      },
      admin: {
        title: 'Admin Dashboard',
        description: 'Platform-wide oversight, analytics, and engagement management',
        path: '/saas/admin/dashboard',
        icon: Shield,
        color: '#1E3A5F',
        features: [
          'Platform analytics',
          'Engagement management',
          'Report generation',
          'User oversight'
        ]
      }
    };
    
    return configs[role] || configs.admin;
  };
  
  const roleConfig = getRoleConfig();
  const RoleIcon = roleConfig.icon;
  
  const getQuickStats = () => {
    if (!stats) return [];
    
    const role = user?.role || 'admin';
    
    if (role === 'corporate') {
      return [
        { label: 'Active Projects', value: stats.activeEngagements || 0, icon: Target },
        { label: 'On Track', value: `${stats.percentOnTrack || 0}%`, icon: CheckCircle2 },
        { label: 'Milestones Due', value: stats.milestonesDue || 0, icon: Clock },
        { label: 'Budget Deployed', value: `₹${(stats.totalBudgetDeployed || 0).toFixed(2)}Cr`, icon: DollarSign }
      ];
    } else if (role === 'ngo') {
      return [
        { label: 'Active Projects', value: stats.activeEngagements || 0, icon: Target },
        { label: 'Deliverables Due', value: stats.updatesDue || 0, icon: Clock },
        { label: 'Pending Approvals', value: stats.pendingApprovals || 0, icon: FileText },
        { label: 'Total Milestones', value: stats.totalMilestones || 0, icon: CheckCircle2 }
      ];
    } else {
      return [
        { label: 'Total Engagements', value: stats.totalEngagements || 0, icon: Users },
        { label: 'Active Projects', value: stats.activeEngagements || 0, icon: Target },
        { label: 'Pending Reviews', value: stats.pendingReviews || 0, icon: FileText },
        { label: 'Budget Managed', value: `₹${(stats.totalBudgetManaged || 0).toFixed(2)}Cr`, icon: DollarSign }
      ];
    }
  };
  
  const quickStats = getQuickStats();
  
  const gettingStartedSteps = [
    {
      title: 'Access Your Dashboard',
      description: 'Navigate to your role-specific dashboard to view all active projects and key metrics.',
      action: 'Go to Dashboard'
    },
    {
      title: 'Review Active Projects',
      description: 'Check project health status, milestones, and upcoming deliverables.',
      action: 'View Projects'
    },
    {
      title: 'Track Progress',
      description: 'Monitor KPIs, financial utilization, and impact metrics in real-time.',
      action: 'View Analytics'
    },
    {
      title: 'Generate Reports',
      description: 'Create comprehensive reports with AI-powered storytelling for stakeholders.',
      action: 'Create Report'
    }
  ];
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-20 flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#14B8A6]"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <div className="pt-20 bg-gradient-to-br from-[#1E3A5F] to-[#14B8A6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-xl" style={{background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(10px)'}}>
                <BarChart3 className="text-white" size={40} strokeWidth={2.5} />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Project Management
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Real-time monitoring, comprehensive reporting, and intelligent insights for CSR projects
            </p>
          </div>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {quickStats.map((stat, index) => {
            const StatIcon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-[#1E3A5F]">{stat.value}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: '#14B8A6', opacity: 0.1}}>
                    <StatIcon className="text-[#14B8A6]" size={20} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Role-Based Dashboard Card */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-8 border-2 border-slate-200 hover:shadow-xl transition-all">
              <div className="flex items-start space-x-6 mb-6">
                <div className="relative">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{background: `linear-gradient(135deg, ${roleConfig.color} 0%, ${roleConfig.color}dd 100%)`}}>
                    <RoleIcon className="text-white" size={40} strokeWidth={2} />
                  </div>
                  {user?.role === 'admin' && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: roleConfig.color}}>
                      <BarChart3 className="text-white" size={16} />
                    </div>
                  )}
                  {user?.role === 'corporate' && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: roleConfig.color}}>
                      <Users className="text-white" size={16} />
                    </div>
                  )}
                  {user?.role === 'ngo' && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: roleConfig.color}}>
                      <Target className="text-white" size={16} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">{roleConfig.title}</h2>
                  <p className="text-slate-600">{roleConfig.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {roleConfig.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="text-[#14B8A6] flex-shrink-0" size={20} />
                    <span className="text-sm text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => navigate(roleConfig.path)}
                className="w-full text-white"
                style={{background: roleConfig.color}}
              >
                Access Dashboard
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
            
            {/* Platform Overview */}
            <div className="mt-8 bg-white rounded-xl shadow-sm p-8 border border-slate-200">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4">Platform Overview</h3>
              <p className="text-slate-600 mb-6">
                The Project Management layer provides end-to-end visibility and control over CSR engagements. 
                From milestone tracking to impact reporting, every aspect of your project is monitored and measured.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'}}>
                    <Target className="text-white" size={36} strokeWidth={2.5} />
                  </div>
                  <h4 className="font-bold text-[#1E3A5F] mb-2 text-lg">Track Progress</h4>
                  <p className="text-sm text-slate-600">Real-time milestone and KPI monitoring</p>
                </div>
                
                <div className="text-center p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'}}>
                    <TrendingUp className="text-white" size={36} strokeWidth={2.5} />
                  </div>
                  <h4 className="font-bold text-[#1E3A5F] mb-2 text-lg">Measure Impact</h4>
                  <p className="text-sm text-slate-600">Comprehensive analytics and insights</p>
                </div>
                
                <div className="text-center p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all bg-gradient-to-br from-white to-slate-50">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'}}>
                    <FileText className="text-white" size={36} strokeWidth={2.5} />
                  </div>
                  <h4 className="font-bold text-[#1E3A5F] mb-2 text-lg">Generate Reports</h4>
                  <p className="text-sm text-slate-600">AI-powered storytelling and documentation</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Getting Started Guide */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-[#14B8A6] to-[#0D9488] rounded-xl shadow-sm p-8 text-white sticky top-24">
              <h3 className="text-xl font-bold mb-4">Getting Started</h3>
              <p className="text-white/90 mb-6 text-sm">
                Follow these steps to make the most of your project management dashboard.
              </p>
              
              <div className="space-y-4">
                {gettingStartedSteps.map((step, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="flex items-start space-x-3 mb-2">
                      <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1 text-sm">{step.title}</h4>
                        <p className="text-xs text-white/80">{step.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-white/20">
                <p className="text-xs text-white/80 mb-3">Need help?</p>
                <Button
                  onClick={() => navigate('/contact')}
                  variant="outline"
                  className="w-full bg-white text-[#14B8A6] hover:bg-white/90 border-0"
                >
                  Contact Support
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Additional Role Cards (Show all roles to admin) */}
        {user?.role === 'admin' && (
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6">All Dashboards</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Sponsor Card */}
              <div className="bg-white rounded-xl shadow-md p-8 border-2 border-slate-200 hover:shadow-xl hover:border-[#14B8A6] transition-all group">
                <div className="relative mb-6">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform" style={{background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'}}>
                    <Building2 className="text-white" size={40} strokeWidth={2} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: '#14B8A6'}}>
                    <Users className="text-white" size={16} />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#1E3A5F] mb-3 text-center">Sponsor Dashboard</h4>
                <p className="text-sm text-slate-600 mb-6 text-center">Monitor funded projects and track impact</p>
                <Button
                  onClick={() => navigate('/saas/sponsor/dashboard')}
                  className="w-full text-white"
                  style={{background: '#14B8A6'}}
                >
                  View Dashboard →
                </Button>
              </div>
              
              {/* Agency Card */}
              <div className="bg-white rounded-xl shadow-md p-8 border-2 border-slate-200 hover:shadow-xl hover:border-[#14B8A6] transition-all group">
                <div className="relative mb-6">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform" style={{background: 'linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)'}}>
                    <Heart className="text-white" size={40} strokeWidth={2} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: '#14B8A6'}}>
                    <Target className="text-white" size={16} />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#1E3A5F] mb-3 text-center">Agency Dashboard</h4>
                <p className="text-sm text-slate-600 mb-6 text-center">Manage projects and submit updates</p>
                <Button
                  onClick={() => navigate('/saas/agency/dashboard')}
                  className="w-full text-white"
                  style={{background: '#14B8A6'}}
                >
                  View Dashboard →
                </Button>
              </div>
              
              {/* Admin Card */}
              <div className="bg-white rounded-xl shadow-md p-8 border-2 border-slate-200 hover:shadow-xl hover:border-[#1E3A5F] transition-all group">
                <div className="relative mb-6">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:scale-110 transition-transform" style={{background: 'linear-gradient(135deg, #1E3A5F 0%, #2D5A7B 100%)'}}>
                    <Shield className="text-white" size={40} strokeWidth={2} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center" style={{background: '#1E3A5F'}}>
                    <BarChart3 className="text-white" size={16} />
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#1E3A5F] mb-3 text-center">Admin Dashboard</h4>
                <p className="text-sm text-slate-600 mb-6 text-center">Platform oversight and analytics</p>
                <Button
                  onClick={() => navigate('/saas/admin/dashboard')}
                  className="w-full text-white"
                  style={{background: '#1E3A5F'}}
                >
                  View Dashboard →
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default ProjectManagementHub;
